import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { ReactTransliterate, TriggerKeys } from "react-transliterate";
import { initialValues, setOrderValues, validate } from "./initialValues";
import { selectToggle } from "../../../redux/slices/NavigationSlice";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  removeCustomer,
  selectCustomer,
} from "../../../redux/slices/CustomerSlice";
import {
  date,
  handleDatePickerFocus,
  localDate,
} from "../../../utils/helper";

// components
import DatePicker from "react-datepicker";
import Pant from "../../../components/Pant";
import Shirt from "../../../components/Shirt";
import api from "../../../utils/axios";
import BillModal from "../../../components/Bill/PrintBillModal";

// safety net: if the session is force-logged-out (e.g. an expired token)
// mid-entry, the in-progress new-order draft survives the reload
const DRAFT_KEY = "dp-order-draft";
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const AddDetails = () => {
  const dispatch = useDispatch();
  const customer = useSelector(selectCustomer);
  const lang = useSelector(selectToggle);
  const { t } = useTranslation();

  // hooks
  const [billNo, setBillNo] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [isPant, setIsPant] = useState(true);
  const [pantOptions, setPantOptions] = useState([]);
  const [shirtOptions, setShirtOptions] = useState([]);

  const [isShirt, setIsShirt] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isWhatsapp, setIsWhatsapp] = useState(false);

  // include / exclude a garment from the order
  const togglePant = () => setIsPant(!isPant);
  const toggleShirt = () => setIsShirt(!isShirt);
  const [order, setOrder] = useState();

  // skip the one repopulation that removeCustomer() triggers right after a
  // successful save, so an updated order does not refill the just-cleared form
  const skipRepopulate = useRef(false);

  // for bill no
  useEffect(() => {
    getBillNo();
  }, []);

  // for edit
  useEffect(() => {
    return () => {
      // a save just cleared the form; do not repopulate from the stale order
      if (skipRepopulate.current) {
        skipRepopulate.current = false;
        return;
      }

      const { order } = customer;
      const { data, type } = order;

      // "save" duplicates an existing order into a brand-new record — preview
      // the next bill number. "edit" updates the existing record in place —
      // keep showing its own bill number.
      if (type === "save") {
        setIsEditing(false);
        getBillNo();
      } else if (type === "edit") {
        setIsEditing(true);
        setBillNo(data.billNo);
      }

      if (Object.keys(order).length !== 0) {
        setIsPant(true);
        setIsShirt(true);
        setOrderValues(formik, order, setPantOptions, setShirtOptions);
      }
    };
  }, [customer]);

  // fetches latest bill no
  const getBillNo = async () => {
    await api.get("order/latest").then((response) => {
      if (response.status === 200) {
        setBillNo(response.data.body.id);
      }
    });
  };

  // options
  const handlePantOptionChange = (value) => {
    setPantOptions((checkedBoxes) => {
      if (checkedBoxes.includes(value)) {
        return checkedBoxes.filter((box) => box !== value);
      } else {
        return [...checkedBoxes, value];
      }
    });
  };

  const handleShirtOptionChange = (value) => {
    setShirtOptions((checkedBoxes) => {
      if (checkedBoxes.includes(value)) {
        return checkedBoxes.filter((box) => box !== value);
      } else {
        return [...checkedBoxes, value];
      }
    });
  };

  // convert options to comma separated string
  function optionsToString(options) {
    let optionsToString = "";
    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        if (value) {
          optionsToString = optionsToString.concat(`${key},`);
        }
      });

      // remove last index
      optionsToString = optionsToString.slice(0, -1);
    }
    return optionsToString;
  }

  const formik = useFormik({
    initialValues: initialValues,
    validate,
    onSubmit: (formValues, { resetForm }) => {
      setLoading(true);

      // work on a copy — `formValues` is formik's live state, and stripping a
      // garment off it would crash the still-mounted Pant/Shirt inputs
      const request = {
        ...formValues,
        customer: {
          ...formValues.customer,
          pant: { ...formValues.customer.pant, pantOptions: pantOptions.join(",") },
          shirt: { ...formValues.customer.shirt, shirtOptions: shirtOptions.join(",") },
        },
      };

      // a garment with no count, no measurements and no details carries nothing
      // for the tailor — don't persist an empty row that would print a blank slip
      const isGarmentEmpty = (g, countKey, detailKey, measureKeys) => {
        if (!g) return true;
        if (Number(g[countKey]) > 0) return false;
        if (measureKeys.some((k) => String(g[k] ?? "").trim() !== "")) return false;
        return String(g[detailKey] ?? "").trim() === "";
      };

      const pantEmpty = isGarmentEmpty(
        request.customer.pant, "pantCount", "pantDetails",
        ["p1","p2","p3","p4","p5","p6","p7","p8","p9","p10"],
      );
      const shirtEmpty = isGarmentEmpty(
        request.customer.shirt, "shirtCount", "shirtDetails",
        ["s1","s2","s3","s4","s5","s6","s7","s8","s9","s10","s11","s12","s13"],
      );

      if (isPant || isShirt) {
        // removing unwanted objects from request
        if (!isPant || pantEmpty) delete request.customer["pant"];

        if (!isShirt || shirtEmpty) delete request.customer["shirt"];

        if (!request.customer.pant && !request.customer.shirt) {
          setLoading(false);
          message.warning(t("common.emptyGarment"));
          return;
        }

        const afterSuccess = () => {
          localStorage.removeItem(DRAFT_KEY);
          resetForm();
          setPantOptions([]);
          setShirtOptions([]);
          // removeCustomer() will fire the [customer] cleanup; tell it not
          // to refill the form we just cleared (bug on updating an order)
          skipRepopulate.current = true;
          setIsEditing(false);
          dispatch(removeCustomer());
          // updating bill number
          getBillNo();
          setLoading(false);
        };

        if (isEditing) {
          // update the existing record in place — same bill number, no new row
          api
            .put(`order/${billNo}`, request)
            .then((response) => {
              if (response.status === 200) {
                // the printed slip is now stale — show the corrected bill
                setOrder(response.data.body);
                setIsOpen(true);
                message.success(t("common.update") + ": " + billNo);
                afterSuccess();
              } else {
                message.error("Error occured : " + response.data.errorMessage);
                setLoading(false);
              }
            })
            .catch((error) => {
              message.error(t("common.errorMessage"));
              setLoading(false);
              console.error(error);
            });
          return;
        }

        const payload = {
          whatsapp: isWhatsapp,
          order: request,
        };

        // add order details (place order)
        api
          .post("order", payload)
          .then((response) => {
            if (response.status === 200) {
              setOrder(response.data.body);
              setIsOpen(true);

              message.success(
                "Order Placed Succesfully for " + response.data.body.billNo,
              );

              // clear the form only after the server confirms success
              afterSuccess();
            } else {
              message.error("Error occured : " + response.data.errorMessage);
              setLoading(false);
            }
          })
          .catch((error) => {
            message.error(t("common.errorMessage"));
            setLoading(false);
            console.error(error);
          });
      } else {
        setLoading(false);
        message.warning("Choose Atlest One Option : Pant or Shirt");
      }
    },
  });

  // restore an unsaved draft (from a forced logout mid-entry) once, on mount —
  // only for a fresh new order, never over an edit/repeat-order prefill
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const { savedAt, values } = JSON.parse(raw);
      if (Date.now() - savedAt > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(DRAFT_KEY);
        return;
      }
      // JSON round-tripping turns Date objects into strings — DatePicker needs real Dates
      if (values.date) values.date = localDate(new Date(values.date));
      if (values.deliveryDate) values.deliveryDate = localDate(new Date(values.deliveryDate));
      if (values?.customer?.name || values?.customer?.mobile) {
        formik.setValues(values);
        message.info(t("common.draftRestored"));
      }
    } catch (e) {
      localStorage.removeItem(DRAFT_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // autosave the in-progress form so it survives a forced logout
  useEffect(() => {
    const timer = setTimeout(() => {
      const hasContent = formik.values?.customer?.name || formik.values?.customer?.mobile;
      try {
        if (hasContent) {
          localStorage.setItem(
            DRAFT_KEY,
            JSON.stringify({ savedAt: Date.now(), values: formik.values }),
          );
        } else {
          localStorage.removeItem(DRAFT_KEY);
        }
      } catch (e) {
        // storage unavailable — draft safety net is best-effort only
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formik.values]);

  return (
    <section className="container-fluid">
      {/* form*/}
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <div className="data-block">
          {/* bill no */}
          <div className="text-end">
            {isEditing && <span className="badge bg-secondary me-2">{t("common.update")}</span>}
            <label>
              {t("common.billNo")} : {billNo}
            </label>
          </div>
          <div className="d-flex">
            {/* block -1 */}
            <div className="d-flex flex-column col-md-4">
              {/* name */}
              <div className="col-md-12 input-block">
                <label>{t("common.name")}</label>
                <ReactTransliterate
                  value={formik.values.customer.name}
                  onChangeText={(text) => {
                    formik.setFieldValue("customer.name", text);
                  }}
                  className="form-control"
                  onFocus={(e) => e.target.select()}
                  lang={lang}
                  enabled={lang !== "en"}
                  triggerKeys={[
                    TriggerKeys.KEY_RETURN,
                    TriggerKeys.KEY_ENTER,
                    TriggerKeys.KEY_SPACE,
                    TriggerKeys.KEY_TAB,
                  ]}
                />
                {formik.errors.name &&
                formik.touched.customer &&
                formik.touched.customer.name ? (
                  <p className="form-validation">{t(formik.errors.name)}</p>
                ) : null}
              </div>
              <div className="col-md-12 input-block">
                {/* address */}
                <label>{t("common.address")}</label>
                <ReactTransliterate
                  renderComponent={(props) => (
                    <textarea cols={5} rows={3} {...props} />
                  )}
                  value={formik.values.customer.address}
                  onFocus={(e) => e.target.select()}
                  onChangeText={(text) => {
                    formik.setFieldValue("customer.address", text);
                  }}
                  className="form-control"
                  lang={lang}
                  enabled={lang !== "en"}
                  triggerKeys={[
                    TriggerKeys.KEY_RETURN,
                    TriggerKeys.KEY_ENTER,
                    TriggerKeys.KEY_SPACE,
                    TriggerKeys.KEY_TAB,
                  ]}
                />
                {formik.errors.address &&
                formik.touched.customer &&
                formik.touched.customer.address ? (
                  <p className="form-validation">{t(formik.errors.address)}</p>
                ) : null}
              </div>
            </div>

            {/* block-2 */}
            <div className="d-flex flex-column offset-md-4 col-md-4">
              {/* date */}
              <div className="col-md-12">
                <label>{t("common.date")}</label>
                <DatePicker
                  id="date"
                  onFocus={handleDatePickerFocus}
                  preventOpenOnFocus={true}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  selected={formik.values.date}
                  className="form-control"
                  disabled
                  onChange={(date) =>
                    formik.setFieldValue("date", localDate(date))
                  }
                />
              </div>
              {/* mobile */}
              <div className="col-md-12 input-block">
                <label>{t("common.mobile")}</label>
                <input
                  id="customer.mobile"
                  type="number"
                  onFocus={(e) => e.target.select()}
                  value={formik.values.customer.mobile}
                  onChange={formik.handleChange}
                  className="form-control"
                />
                {formik.errors.mobile &&
                formik.touched.customer &&
                formik.touched.customer.mobile ? (
                  <p className="form-validation">{t(formik.errors.mobile)}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="dp-toggle-block">
            {/* whatsapp toggle */}
            <button
              type="button"
              className={isWhatsapp ? "dp-wa on" : "dp-wa"}
              onClick={() => setIsWhatsapp(!isWhatsapp)}
              aria-pressed={isWhatsapp}
            >
              <img src="/images/whatsapp.png" alt="" width={20} height={20} />
              <span>{t("common.whatsappMessage")}</span>
              <span className="dp-wa-switch" aria-hidden="true" />
            </button>
          </div>

          {/* divider */}
          <hr />

          {/* ===== garment section ===== */}
          <div className="dp-garment-section">
            {/* include toggle */}
            <div className="dp-garment-toggle">
              <button
                type="button"
                className={isPant ? "dp-gt on" : "dp-gt"}
                onClick={togglePant}
                aria-pressed={isPant}
              >
                <img src="/images/pants.png" alt="" />
                <span>{t("common.pant")}</span>
                <span className="dp-gt-ck" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 8 3.5 3.5L13 4.5" />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                className={isShirt ? "dp-gt on" : "dp-gt"}
                onClick={toggleShirt}
                aria-pressed={isShirt}
              >
                <img src="/images/shirt.png" alt="" />
                <span>{t("common.shirt")}</span>
                <span className="dp-gt-ck" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m3 8 3.5 3.5L13 4.5" />
                  </svg>
                </span>
              </button>
            </div>

            {/* detail panels — stacked, both visible when both are selected */}
            <div className="dp-garment-panels">
              {isPant ? (
                <Pant
                  formik={formik}
                  options={pantOptions}
                  handlePantOptionChange={handlePantOptionChange}
                />
              ) : null}

              {isShirt ? (
                <Shirt
                  formik={formik}
                  options={shirtOptions}
                  handleShirtOptionChange={handleShirtOptionChange}
                />
              ) : null}
            </div>
          </div>

          {/* bottom */}
          <div className="row g-3">
            {/* delivery date */}
            <div className="col-md-2">
              <label>{t("common.delieveryDate")}</label>
              <DatePicker
                id="deliveryDate"
                minDate={date}
                onFocus={handleDatePickerFocus}
                preventOpenOnFocus={true}
                dateFormat="dd/MM/yyyy"
                showYearDropdown
                showMonthDropdown
                selected={formik.values.deliveryDate}
                className="form-control m-0"
                onChange={(date) =>
                  formik.setFieldValue("deliveryDate", localDate(date))
                }
              />

              {formik.errors.deliveryDate && formik.touched.deliveryDate ? (
                <p className="form-validation">
                  {t(formik.errors.deliveryDate)}
                </p>
              ) : null}
            </div>
            {/* clothe advance button */}
            <div className="col-md-2">
              <label>{t("common.clothAdvance")}</label>
              <input
                type="number"
                id="clothAdvance"
                onFocus={(e) => e.target.select()}
                value={formik.values.clothAdvance}
                className="form-control"
                onChange={formik.handleChange}
              />
            </div>
            {/* advance button */}
            <div className="col-md-2">
              <label>{t("common.advance")}</label>
              <input
                type="number"
                id="advance"
                onFocus={(e) => e.target.select()}
                value={formik.values.advance}
                className="form-control"
                onChange={formik.handleChange}
              />
            </div>
            {/* amount */}
            <div className="col-md-2">
              <label>{t("common.amount")}</label>
              <input
                type="number"
                onFocus={(e) => e.target.select()}
                id="amount"
                value={formik.values.amount}
                className="form-control"
                onChange={formik.handleChange}
              />
              {formik.errors.amount && formik.touched.amount ? (
                <p className="form-validation">{t(formik.errors.amount)}</p>
              ) : null}
            </div>
            {/* submit button */}
            <div className="col-md-2">
              <button type="submit" className="button-primary">
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="m-2">{t("common.loading")}</span>
                  </>
                ) : (
                  <span className="m-2"> {isEditing ? t("common.update") : t("common.save")}</span>
                )}
              </button>
            </div>
            {/* clear button */}
            <div className="col-md-2">
              <button
                type="reset"
                className="button-secondary"
                onClick={() => {
                  localStorage.removeItem(DRAFT_KEY);
                  setIsEditing(false);
                  getBillNo();
                }}
              >
                {t("common.clear")}
              </button>
            </div>
          </div>
        </div>
      </form>
      <BillModal order={order} isOpen={isOpen} setIsOpen={setIsOpen} />
    </section>
  );
};

export default AddDetails;
